import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FiArrowLeft, FiCheckCircle, FiInfo, FiDollarSign, FiActivity, FiFileText, FiSend } from 'react-icons/fi';
import { callApi } from '../../callApi';
import { AppContext } from '../AppContext';
import { useToast } from '@chakra-ui/react';

const FqClaimDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check session storage for mock data if location state is empty
  const getInitialData = () => {
    if (location.state?.claimData) return location.state.claimData;
    const sessionData = sessionStorage.getItem('mockClaimData');
    if (sessionData) {
      sessionStorage.removeItem('mockClaimData'); // Clear it after reading
      return JSON.parse(sessionData);
    }
    return null;
  };

  const [claimData, setClaimData] = useState(getInitialData());
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  const [isGeneratingWraparound, setIsGeneratingWraparound] = useState(false);
  const [isDraftingAppeal, setIsDraftingAppeal] = useState(false);
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    if (!claimData) {
      // If no data was passed via router state, go back to intake
      navigate('/fq/intake');
    } else if (!claimData.rules_fired && accessToken) {
      // If data exists but hasn't been evaluated, run the rules engine
      runRulesEngine(claimData);
    }
  }, [claimData, navigate, accessToken]);

  const checkEligibility = async () => {
    if (!accessToken) return;
    setIsCheckingEligibility(true);
    try {
      const response = await callApi('/api/fq/eligibility', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: claimData?.patient?.member_id })
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.data) {
          setEligibilityData(data.data);
        }
      }
    } catch (error) {
      console.error("Failed to check eligibility:", error);
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const runRulesEngine = async (data) => {
    if (!accessToken) return;
    setIsEvaluating(true);
    try {
      const response = await callApi('/api/fq/evaluate-claim', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        const responseData = await response.json();
        if (responseData && responseData.data) {
          setClaimData(responseData.data);
        }
      }
    } catch (error) {
      console.error("Failed to evaluate claim:", error);
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleGenerateWraparound = () => {
    setIsGeneratingWraparound(true);
    setTimeout(() => {
      setIsGeneratingWraparound(false);
      toast({
        title: "Wraparound Claim Generated",
        description: `Secondary claim for $${wraparound.wraparound_owed.toFixed(2)} has been queued for submission to Medi-Cal.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }, 1500);
  };

  const handleDraftAppeal = () => {
    setIsDraftingAppeal(true);
    setTimeout(() => {
      setIsDraftingAppeal(false);
      toast({
        title: "Appeal Letter Drafted",
        description: "Evidence-based appeal citing DHCS Provider Manual has been saved to the claim file.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    }, 1500);
  };

  if (!claimData) return null;

  const { patient, provider, claim, rules_fired, wraparound } = claimData;

  return (
    <Box maxW="7xl" mx="auto">
      <Button 
        leftIcon={<FiArrowLeft />} 
        variant="ghost" 
        mb={6} 
        onClick={() => navigate('/fq/intake')}
      >
        Back to Intake
      </Button>

      <Flex justify="space-between" align="flex-end" mb={8}>
        <Box>
          <Heading size="lg" mb={2}>Claim Analysis</Heading>
          <Text color="gray.500">
            Patient: {patient?.first_name} {patient?.last_name} | Provider: {provider?.name}
          </Text>
        </Box>
        <Badge colorScheme="purple" fontSize="md" p={2} borderRadius="md">
          {claimData.type}
        </Badge>
      </Flex>

      <Flex gap={8} direction={{ base: 'column', lg: 'row' }}>
        
        {/* Left Column: Claim Data */}
        <VStack flex="1" spacing={6} align="stretch">
          <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
            <CardHeader pb={0}>
              <Heading size="md" display="flex" alignItems="center">
                <Icon as={FiActivity} mr={2} color="blue.500" />
                Procedures & Diagnoses
              </Heading>
            </CardHeader>
            <CardBody>
              <Box mb={6}>
                <Text fontWeight="bold" mb={2} color="gray.500" fontSize="sm" textTransform="uppercase">
                  Diagnoses (ICD-10)
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  {claim?.diagnoses?.map((dx, i) => (
                    <Badge key={i} colorScheme="blue" variant="subtle" fontSize="sm" p={1}>
                      {dx}
                    </Badge>
                  ))}
                </HStack>
              </Box>
              
              <Box>
                <Text fontWeight="bold" mb={2} color="gray.500" fontSize="sm" textTransform="uppercase">
                  Procedures (CPT)
                </Text>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Code</Th>
                      <Th>Modifiers</Th>
                      <Th isNumeric>Charge</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {claim?.procedures?.map((proc, i) => (
                      <Tr key={i}>
                        <Td fontWeight="medium">{proc.cpt}</Td>
                        <Td>
                          {proc.modifiers?.map(m => (
                            <Badge key={m} colorScheme="orange" mr={1}>{m}</Badge>
                          ))}
                        </Td>
                        <Td isNumeric>${proc.charge?.toFixed(2)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>

          {wraparound && (
            <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
              <CardHeader pb={0}>
                <Heading size="md" display="flex" alignItems="center">
                  <Icon as={FiDollarSign} mr={2} color="green.500" />
                  Wraparound Computation
                </Heading>
              </CardHeader>
              <CardBody>
                <Flex justify="space-between" align="center" mb={6}>
                  <Stat>
                    <StatLabel>PPS Rate</StatLabel>
                    <StatNumber fontSize="xl">${wraparound.pps_rate?.toFixed(2)}</StatNumber>
                  </Stat>
                  <Text fontSize="2xl" color="gray.400">-</Text>
                  <Stat px={4}>
                    <StatLabel>MCP Paid</StatLabel>
                    <StatNumber fontSize="xl">${wraparound.mcp_paid?.toFixed(2)}</StatNumber>
                  </Stat>
                  <Text fontSize="2xl" color="gray.400">=</Text>
                  <Stat textAlign="right">
                    <StatLabel color="green.500" fontWeight="bold">Wraparound Owed</StatLabel>
                    <StatNumber fontSize="2xl" color="green.500">
                      ${wraparound.wraparound_owed?.toFixed(2)}
                    </StatNumber>
                  </Stat>
                </Flex>
                <Divider mb={4} />
                <Flex justify="flex-end">
                  <Button 
                    colorScheme="green" 
                    leftIcon={<FiSend />}
                    isLoading={isGeneratingWraparound}
                    loadingText="Generating..."
                    onClick={handleGenerateWraparound}
                  >
                    Generate Wraparound Claim
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          )}

          {/* Mock 835 Denial Scenario for Demo */}
          {claimData.type === '835' && claimData.denial_reason && (
            <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
              <CardHeader pb={0}>
                <Heading size="md" display="flex" alignItems="center" color="red.600">
                  <Icon as={FiInfo} mr={2} />
                  835 Remittance Denial
                </Heading>
              </CardHeader>
              <CardBody>
                <Alert status="error" variant="left-accent" mb={4}>
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Claim Denied</AlertTitle>
                    <AlertDescription>{claimData.denial_reason}</AlertDescription>
                  </Box>
                </Alert>
                <Flex justify="flex-end">
                  <Button 
                    colorScheme="blue" 
                    leftIcon={<FiFileText />}
                    isLoading={isDraftingAppeal}
                    loadingText="Drafting..."
                    onClick={handleDraftAppeal}
                  >
                    Draft Appeal Letter
                  </Button>
                </Flex>
              </CardBody>
            </Card>
          )}
        </VStack>

        {/* Right Column: Rules Engine */}
        <VStack flex="1" spacing={6} align="stretch">
          
          {/* Eligibility / COB / PCP-IPA Slice */}
          <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm">
            <CardHeader pb={0}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading size="md" display="flex" alignItems="center">
                    <Icon as={FiInfo} mr={2} color="teal.500" />
                    Coverage Check (270/271)
                  </Heading>
                  <Text color="gray.500" fontSize="sm" mt={1}>
                    Pre-submission denial prevention.
                  </Text>
                </Box>
                {!eligibilityData && (
                  <Button size="sm" colorScheme="teal" isLoading={isCheckingEligibility} onClick={checkEligibility}>
                    Run Check
                  </Button>
                )}
              </Flex>
            </CardHeader>
            <CardBody>
              {eligibilityData ? (
                <VStack align="stretch" spacing={3}>
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Status:</Text>
                    <Badge colorScheme="green">{eligibilityData.status}</Badge>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Payer:</Text>
                    <Text fontWeight="medium" fontSize="sm">{eligibilityData.payer}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Managed Care Plan:</Text>
                    <Text fontWeight="medium" fontSize="sm">{eligibilityData.managed_care_plan}</Text>
                  </Flex>
                  <Divider my={1} />
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Assigned PCP:</Text>
                    <Text fontWeight="medium" fontSize="sm" color={provider?.name !== eligibilityData.assigned_pcp ? "red.500" : "inherit"}>
                      {eligibilityData.assigned_pcp}
                    </Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Assigned IPA:</Text>
                    <Text fontWeight="medium" fontSize="sm">{eligibilityData.assigned_ipa}</Text>
                  </Flex>
                  
                  {provider?.name !== eligibilityData.assigned_pcp && (
                    <Alert status="warning" size="sm" mt={2} borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <AlertTitle fontSize="sm">PCP Mismatch Detected</AlertTitle>
                        <AlertDescription fontSize="xs">
                          Rendering provider ({provider?.name || 'Unknown'}) does not match assigned PCP. This will likely result in a denial.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              ) : (
                <Text color="gray.500" fontSize="sm" fontStyle="italic">
                  Coverage check not yet run.
                </Text>
              )}
            </CardBody>
          </Card>

          <Card bg={bgColor} borderColor={borderColor} borderWidth="1px" shadow="sm" h="100%">
            <CardHeader pb={0}>
              <Heading size="md" display="flex" alignItems="center">
                <Icon as={FiCheckCircle} mr={2} color="purple.500" />
                FQHC Rules Engine
              </Heading>
              <Text color="gray.500" fontSize="sm" mt={1}>
                California Medi-Cal logic applied to this encounter.
              </Text>
            </CardHeader>
            <CardBody>
              {isEvaluating ? (
                <Text>Evaluating rules...</Text>
              ) : (
                <VStack spacing={4} align="stretch">
                  {rules_fired?.map((rule, i) => (
                    <Alert key={i} status="info" variant="left-accent" borderRadius="md" flexDirection="column" alignItems="flex-start" p={4}>
                      <Flex align="center" mb={2}>
                        <AlertIcon />
                        <AlertTitle fontSize="md">{rule.rule_name}</AlertTitle>
                      </Flex>
                      <AlertDescription fontSize="sm" w="100%">
                        <Text mb={2}>{rule.description}</Text>
                        <Box bg="whiteAlpha.500" p={2} borderRadius="sm" mb={2}>
                          <Text fontWeight="bold" color="blue.700" fontSize="xs">ACTION TAKEN:</Text>
                          <Text color="blue.800">{rule.action}</Text>
                        </Box>
                        <Text fontSize="xs" color="gray.500" fontStyle="italic">
                          Citation: {rule.citation}
                        </Text>
                      </AlertDescription>
                    </Alert>
                  ))}
                  
                  {(!rules_fired || rules_fired.length === 0) && (
                    <Text color="gray.500" fontStyle="italic">No specific FQHC rules fired for this claim.</Text>
                  )}
                </VStack>
              )}
            </CardBody>
          </Card>
        </VStack>

      </Flex>
    </Box>
  );
};

export default FqClaimDetail;
